import React, { useState, useEffect } from 'react';
import { Button, Form, FormGroup, FormLabel, FormControl, FormCheck, Row, Col, Container, InputGroup } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import '../AdminDashboard.css';
import axios from 'axios';
import Cookies from 'js-cookie';

const AccountAccessPage = ({ ViewedPage, app_url }) => {
  const { shop } = useParams();
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [availablePages, setAvailablePages] = useState([]);
  const [pagesMapping, setPagesMapping] = useState({}); // Store page name -> ID mapping
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [sectionRearrangementEnabled, setSectionRearrangementEnabled] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState({ value: '', label: '' });
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('');

  // Pricing plan access control
  const [pricingPlans, setPricingPlans] = useState([]);
  const [pricingPlansLoading, setPricingPlansLoading] = useState(false);
  const [allShops, setAllShops] = useState([]);
  // Local, editable copy of plans for "Save Changes" flow
  const [pendingPlans, setPendingPlans] = useState([]);
  const [hasPlanChanges, setHasPlanChanges] = useState(false);
  const [savingPlanAccess, setSavingPlanAccess] = useState(false);

  // Current subscription info
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  useEffect(() => {
    ViewedPage(101);
    const loadData = async () => {
      try {
        await Promise.all([
          fetchAccountData(),
          fetchAvailablePages(),
          fetchCurrencies(),
          fetchPricingPlans(),
          fetchAllShops(),
          fetchShopSubscription()
        ]);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [shop, ViewedPage]);

  const fetchAccountData = async () => {
    try {
      const response = await axios.get(app_url + '/api/admin/stores', { withCredentials: true });
      const data = response.data;
      if (data.success) {
        const foundAccount = data.data.find(store => store.shop === shop);
        console.log('Account found', foundAccount);
        if (foundAccount) {
          setAccount(foundAccount);
          const toggleValue =
            foundAccount?.personalization_settings?.section_rearrangement?.enabled ??
            foundAccount?.is_section_rearrangement_enabled ??
            false;
          setSectionRearrangementEnabled(!!toggleValue);
          
          // Set selected currency from account data
          if (foundAccount.currency) {
            setSelectedCurrency(foundAccount.currency);
          }
        } else {
          setMessage({ type: 'error', text: 'Account not found' });
        }
      }
    } catch (error) {
      console.error('Error fetching account data:', error);
      setMessage({ type: 'error', text: 'Failed to fetch account data' });
      throw error; // Re-throw to be caught by Promise.all
    }
  };

  const fetchCurrencies = async () => {
    try {
      const response = await axios.get(app_url + '/api/admin/currencies', { withCredentials: true });
      const data = response.data;
      if (data.success && data.data) {
        setCurrencies(data.data);
      }
    } catch (error) {
      console.error('Error fetching currencies:', error);
      // Don't throw error, just log it - currencies are optional
    }
  };

  const fetchAvailablePages = async () => {
    try {
      const response = await axios.get(app_url + '/api/admin/available-pages', { withCredentials: true });
      const data = response.data;

      let pageMappingData = null;

      if (data.success && data.data && data.data.all_pages_mapping) {
        pageMappingData = data.data.all_pages_mapping;
      } else if (data.all_pages_mapping) {
        pageMappingData = data.all_pages_mapping;
      }

      if (pageMappingData && typeof pageMappingData === 'object') {
        const pagesArray = Object.keys(pageMappingData);
        setAvailablePages(pagesArray);
        setPagesMapping(pageMappingData); // Store the complete mapping
      } else {
        setAvailablePages([]);
        setPagesMapping({});
      }
    } catch (error) {
      console.error('Error fetching available pages:', error);
      setAvailablePages([]);
      setPagesMapping({});
      throw error; // Re-throw to be caught by Promise.all
    }
  };

  const fetchPricingPlans = async () => {
    try {
      setPricingPlansLoading(true);
      const response = await axios.get(app_url + '/api/subscriptions/plans', { withCredentials: true });
      const data = response.data;
      if (data.success && Array.isArray(data.data)) {
        setPricingPlans(data.data);
        setPendingPlans(data.data);
        setHasPlanChanges(false);
      } else {
        setPricingPlans([]);
        setPendingPlans([]);
        setHasPlanChanges(false);
      }
    } catch (error) {
      console.error('Error fetching pricing plans:', error);
      setPricingPlans([]);
      setPendingPlans([]);
      setHasPlanChanges(false);
    } finally {
      setPricingPlansLoading(false);
    }
  };

  const fetchAllShops = async () => {
    try {
      const response = await axios.get(app_url + '/api/subscriptions/shops', { withCredentials: true });
      const data = response.data;
      if (data.success && Array.isArray(data.data)) {
        setAllShops(data.data);
      } else {
        setAllShops([]);
      }
    } catch (error) {
      console.error('Error fetching shops:', error);
      setAllShops([]);
    }
  };

  const fetchShopSubscription = async () => {
    try {
      setSubscriptionLoading(true);
      const response = await axios.get(app_url + '/api/subscriptions/my-subscription', {
        params: { shop_domain: shop },
        withCredentials: true
      });
      const data = response.data;
      if (data.success) {
        setSubscription(data.data);
      } else {
        setSubscription(null);
      }
    } catch (error) {
      console.error('Error fetching shop subscription:', error);
      setSubscription(null);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handlePageToggle = (page, checked) => {
    if (!account) return;

    const updatedAccount = { ...account };
    if (!updatedAccount.allowed_pages) updatedAccount.allowed_pages = {};
    updatedAccount.allowed_pages[page] = checked;
    setAccount(updatedAccount);
  };

  const shopKey = account ? account.shop.replace(/\.myshopify\.com$/, '') : '';

  const isPlanVisibleToShop = (plan) => {
    if (!plan) return false;
    if (plan.price === 0) return true;
    const visible = plan.visible_to_shops;
    if (!visible || !Array.isArray(visible) || visible.length === 0) return true;
    return visible.some(s => s.replace(/\.myshopify\.com$/, '') === shopKey);
  };

  // Toggle a plan's visibility locally; changes are only persisted when clicking "Save Changes"
  const handlePlanVisibilityToggle = (planId, checked) => {
    if (!account) return;
    const key = account.shop ? account.shop.replace(/\.myshopify\.com$/, '') : '';
    if (!key) return;

    setPendingPlans(prev =>
      prev.map(plan => {
        if (plan._id !== planId) return plan;
        if (plan.price === 0) return plan; // free plan always visible

        const currentList = plan.visible_to_shops || [];
        let newVisibleToShops;

        if (checked) {
          // ensure shop is included once
          if (currentList.some(s => s.replace(/\.myshopify\.com$/, '') === key)) {
            newVisibleToShops = currentList;
          } else {
            newVisibleToShops = [...currentList, key];
          }
        } else {
          if (currentList.length === 0) {
            // interpret "no restriction" as "all shops"; remove this shop
            const allShopIds = allShops.map(s => (s.shop || s).replace(/\.myshopify\.com$/, ''));
            newVisibleToShops = allShopIds.filter(s => s !== key);
          } else {
            newVisibleToShops = currentList.filter(
              s => s.replace(/\.myshopify\.com$/, '') !== key
            );
          }
        }

        setHasPlanChanges(true);
        return { ...plan, visible_to_shops: newVisibleToShops };
      })
    );
  };

  // Persist all pending pricing plan visibility changes for this shop
  const handleSavePlanVisibilityChanges = async () => {
    if (!pendingPlans || !pendingPlans.length || !pricingPlans.length || !account) return;
    setSavingPlanAccess(true);
    try {
      const updates = [];

      for (const pending of pendingPlans) {
        const original = pricingPlans.find(p => p._id === pending._id);
        if (!original) continue;

        const origList = original.visible_to_shops || [];
        const pendingList = pending.visible_to_shops || [];

        const sameLength = origList.length === pendingList.length;
        const sameContent =
          sameLength &&
          origList.every(s => pendingList.includes(s));

        if (!sameContent) {
          updates.push({
            id: pending._id,
            visible_to_shops: pendingList
          });
        }
      }

      for (const u of updates) {
        await axios.post(
          `${app_url}/api/subscriptions/plans/${u.id}/visibility`,
          { visible_to_shops: u.visible_to_shops },
          { withCredentials: true }
        );
      }

      if (updates.length > 0) {
        setPricingPlans(pendingPlans);
      }
      setHasPlanChanges(false);
      setMessage({ type: 'success', text: 'Pricing plan access updated successfully.' });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error?.response?.data?.message || 'Error saving pricing plan access changes'
      });
    } finally {
      setSavingPlanAccess(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Filter and sort functions
  const getFilteredPages = () => {
    let filtered = availablePages;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(page => 
        page.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    if (sortBy) {
      filtered = [...filtered].sort((a, b) => {
        switch (sortBy) {
          case 'name-asc':
            return a.localeCompare(b);
          case 'name-desc':
            return b.localeCompare(a);
          case 'status-asc':
            return (account.allowed_pages?.[a] || false) - (account.allowed_pages?.[b] || false);
          case 'status-desc':
            return (account.allowed_pages?.[b] || false) - (account.allowed_pages?.[a] || false);
          default:
            return 0;
        }
      });
    }

    return filtered;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!account) return;

    try {
      setSaving(true);

      // First: save feature access / account settings
      const allowedPagesWithIds = {};
      if (account.allowed_pages) {
        Object.keys(account.allowed_pages).forEach(pageName => {
          if (account.allowed_pages[pageName] && pagesMapping[pageName]) {
            allowedPagesWithIds[pageName] = pagesMapping[pageName];
          }
        });
      }

      const response = await axios.post(
        app_url + '/api/admin/update-access',
        {
          shop: account.shop,
          allowed_pages: allowedPagesWithIds,
          is_section_rearrangement_enabled: !!sectionRearrangementEnabled,
          currency: selectedCurrency
        },
        { withCredentials: true }
      );

      const data = response.data;
      if (!data.success) {
        setMessage({ type: 'error', text: data.message || 'Failed to update access' });
        setSaving(false);
        return;
      }

      // Second: save pricing plan visibility changes (if any) using same button
      if (hasPlanChanges) {
        await handleSavePlanVisibilityChanges();
      }

      setMessage({ type: 'success', text: 'Access updated successfully!' });
      // Update the allowed_pages_json cookie inline (no reload yet) so the new
      // features are reflected if the admin shares the same browser as the store.
      try {
        const shopKey = (account.shop || '').replace('.myshopify.com', '');
        const refreshResp = await axios.get(
          `${app_url}/api/subscriptions/refresh-allowed-pages`,
          { params: { shop_domain: shopKey }, withCredentials: true }
        );
        if (refreshResp.data?.success) {
          const freshMap = refreshResp.data.data?.allowed_pages_json || {};
          const existing = (() => {
            try { return JSON.parse(Cookies.get('allowed_pages_json') || '{}'); } catch { return {}; }
          })();
          Cookies.set('allowed_pages_json', JSON.stringify({ ...existing, ...freshMap }), {
            sameSite: process.env.REACT_APP_WENV !== 'local' ? 'strict' : '',
            secure: process.env.REACT_APP_WENV !== 'local',
            domain: process.env.REACT_APP_WENV !== 'local' ? '.cognitiveux.ai' : '',
            path: '/',
            expires: 0.5,
          });
        }
      } catch (cookieErr) {
        console.warn('Failed to refresh cookie after admin access update', cookieErr);
      }
      // Navigate back to admin list with a full page load so the new cookie is applied
      window.location.href = '/admin';
    } catch (error) {
      setMessage({ type: 'error', text: 'Error updating access' });
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/admin');
  };

  if (loading) {
    return (
      <div className="main-container">
        <div className="admin-loading-inline">
          <div className="admin-loading-content">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading account data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!account) {
    return (
      <div className="main-container">
        <div className="admin-header">
          <h1 className="admin-title">Account Not Found</h1>
          <p className="admin-subtitle">The requested account could not be found</p>
          <Button variant="primary" onClick={handleBack} className="admin-btn">
            Back to Admin Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="main-container">
      <div className="admin-header">
        <div className="admin-header-content">
          <div className="admin-header-left">
            <h1 className="admin-title">Manage Account Access</h1>
            <p className="admin-subtitle">
              Configure permissions for: <strong>{account.shop_name || account.shop}</strong>
            </p>
          </div>
          <div className="admin-header-right">
            <Button variant="outline-secondary" onClick={handleBack} className="admin-btn">
              ← Back to Dashboard
            </Button>
          </div>
        </div>
      </div>

      {message.text && (
        <div className={`admin-message admin-message-${message.type}`}>
          {message.text}
          <Button 
            variant="link" 
            className="admin-message-close"
            onClick={() => setMessage({ type: '', text: '' })}
          >
            ×
          </Button>
        </div>
      )}

      <Container fluid className="admin-content">
        <Row>
          <Col className="mx-auto">
            <div className="admin-section">
              <div className="admin-section-header">
                <h3 className="admin-section-title">Account Information</h3>
              </div>
              
              <div className="admin-info-grid">
                <div className="admin-info-item">
                  <label className="admin-info-label">Shop:</label>
                  <span className="admin-info-value">{account.shop}</span>
                </div>
                <div className="admin-info-item">
                  <label className="admin-info-label">Shop Name:</label>
                  <span className="admin-info-value">{account.shop_name || 'N/A'}</span>
                </div>
                <div className="admin-info-item">
                  <label className="admin-info-label">Account Type:</label>
                  <span className="admin-info-value">
                    <span className={`account-type-badge account-type-${account.account_type || 'live'}`}>
                      {(account.account_type || 'live').charAt(0).toUpperCase() + (account.account_type || 'live').slice(1)}
                    </span>
                  </span>
                </div>
                <div className="admin-info-item">
                  <label className="admin-info-label">Features Enabled:</label>
                  <span className="admin-info-value">
                    {account.allowed_pages ? Object.keys(account.allowed_pages).filter(page => account.allowed_pages[page]).length : 0} / {availablePages.length}
                  </span>
                </div>
              </div>
            </div>


            <div className="admin-section">
              <div className="admin-section-header">
                <h3 className="admin-section-title">Pricing Plan Access Control</h3>
                <p className="admin-section-description">
                  Choose which subscription plans this shop can see on the pricing page. Adjust checkboxes and click Save Changes to apply updates.
                </p>
              </div>
              {pricingPlansLoading ? (
                <div className="admin-loading-inline">
                  <div className="admin-loading-content">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading pricing plans...</p>
                  </div>
                </div>
              ) : pendingPlans.length === 0 ? (
                <p className="admin-no-data">No pricing plans found. Create plans in Admin → Pricing Plans.</p>
              ) : (
                <div className="admin-pages-grid">
                  {pendingPlans.map((plan) => {
                    const visible = isPlanVisibleToShop(plan);
                    const isFree = plan.price === 0;
                    const isUpdating = savingPlanAccess;
                    const priceLabel = isFree
                      ? 'Free'
                      : `${plan.currency === 'INR' ? '₹' : '$'}${plan.price}/${plan.interval === 'yearly' ? 'yr' : 'mo'}`;
                    return (
                      <div key={plan._id} className="admin-page-item">
                        <FormCheck
                          type="checkbox"
                          id={`plan-${plan._id}`}
                          checked={visible}
                          disabled={isFree || isUpdating}
                          onChange={(e) => handlePlanVisibilityToggle(plan._id, e.target.checked)}
                          label={
                            <span>
                              {plan.name}
                              <small className="text-muted d-block">{priceLabel}{isFree ? ' (always visible to all shops)' : ''}</small>
                            </span>
                          }
                          className="admin-checkbox-large"
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="admin-section">
              <div className="admin-section-header">
                <h3 className="admin-section-title">Current Subscription</h3>
                <p className="admin-section-description">
                  Overview of this shop&apos;s active subscription plan.
                </p>
              </div>
              {subscriptionLoading ? (
                <div className="admin-loading-inline">
                  <div className="admin-loading-content">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading subscription details...</p>
                  </div>
                </div>
              ) : !subscription ? (
                <p className="admin-no-data">This shop does not have an active subscription.</p>
              ) : (
                <div className="admin-info-grid">
                  <div className="admin-info-item">
                    <label className="admin-info-label">Plan</label>
                    <span className="admin-info-value">
                      {subscription.plan?.name || subscription.plan_name || 'Unknown plan'}
                    </span>
                  </div>
                  <div className="admin-info-item">
                    <label className="admin-info-label">Price</label>
                    <span className="admin-info-value">
                      {subscription.plan_price === 0
                        ? 'Free'
                        : `${subscription.plan_currency === 'INR' ? '₹' : '$'}${subscription.plan_price}/${subscription.plan_interval === 'yearly' ? 'yr' : 'mo'}`}
                    </span>
                  </div>
                  <div className="admin-info-item">
                    <label className="admin-info-label">Status</label>
                    <span className="admin-info-value">
                      {subscription.status
                        ? subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="admin-info-item">
                    <label className="admin-info-label">Current Period</label>
                    <span className="admin-info-value">
                      {formatDate(subscription.current_start)} → {formatDate(subscription.current_end)}
                    </span>
                  </div>
                </div>
              )}
            </div>
            
            <div className="admin-section">
              <div className="admin-section-header">
                <h3 className="admin-section-title">Feature Access Control</h3>
                <p className="admin-section-description">
                  Enable or disable specific features for this account
                </p>
              </div>
              
              <Form onSubmit={handleSave}>
                <FormGroup className="mb-3">
                  <FormLabel>Currency</FormLabel>
                  <FormControl
                    as="select"
                    value={selectedCurrency.value || ''}
                    onChange={(e) => {
                      const selected = currencies.find(c => c.value === e.target.value);
                      if (selected) {
                        setSelectedCurrency(selected);
                      }
                    }}
                    className="admin-currency-select"
                  >
                    <option value="">Select Currency</option>
                    {currencies.map((currency) => (
                      <option key={currency.value} value={currency.value}>
                        {currency.label}
                      </option>
                    ))}
                  </FormControl>
                  <small className="text-muted">
                    Select the currency for this account
                  </small>
                </FormGroup>

                <FormGroup className="mb-3">
                  <FormCheck
                    type="switch"
                    id="section-rearrangement-toggle"
                    label="Enable Section Rearrangement"
                    checked={sectionRearrangementEnabled}
                    onChange={(e) => setSectionRearrangementEnabled(e.target.checked)}
                  />
                  <small className="text-muted">
                    Controls visibility and payload of section rearrangement across personalized pages.
                  </small>
                </FormGroup>
                {/* Search and Filter Controls */}
                <Row className="admin-controls-row">
                  <Col md={6}>
                    <InputGroup className="admin-search-group">
                      <InputGroup.Text className="admin-search-icon">🔍</InputGroup.Text>
                      <FormControl
                        type="text"
                        placeholder="Search features..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="admin-search-input"
                      />
                    </InputGroup>
                  </Col>
                  <Col md={6}>
                    <FormControl
                      as="select"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="admin-sort-select"
                    >
                      <option value="">Sort by</option>
                      <option value="name-asc">Feature Name (A-Z)</option>
                      <option value="name-desc">Feature Name (Z-A)</option>
                      <option value="status-asc">Status (Disabled First)</option>
                      <option value="status-desc">Status (Enabled First)</option>
                    </FormControl>
                  </Col>
                </Row>

                <div className="admin-pages-grid">
                  {getFilteredPages().length === 0 ? (
                    <p className="admin-no-data">
                      {searchTerm ? 'No features match your search criteria' : 'No features found'}
                    </p>
                  ) : (
                    getFilteredPages().map(page => (
                      <div key={page} className="admin-page-item">
                        <FormCheck
                          type="checkbox"
                          id={`page-${page}`}
                          checked={account.allowed_pages?.[page] || false}
                          onChange={(e) => handlePageToggle(page, e.target.checked)}
                          label={page.charAt(0).toUpperCase() + page.slice(1).replace(/_/g, ' ')}
                          className="admin-checkbox-large"
                        />

                      </div>
                    ))
                  )}
                </div>

                <div className="admin-form-actions">
                  <Button 
                    type="button" 
                    variant="outline-secondary" 
                    onClick={handleBack}
                    className="admin-btn"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={saving} 
                    className="admin-btn admin-btn-primary"
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </Form>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};



export default AccountAccessPage;
